import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { BookOpen, Download, ListMusic, Pause, Play } from "lucide-react";
import { API } from "../../lib/api";
import { Countdown } from "../../lib/bookmark";

function Playlist({ b }) {
  const tracks = b.poems.filter((p) => p.has_audio);
  const [i, setI] = useState(0);
  const [playing, setPlaying] = useState(false);
  const ref = useRef(null);
  if (tracks.length < 2) return null;
  const play = (n) => { setI(n); setPlaying(true); setTimeout(() => ref.current?.play().catch(() => setPlaying(false)), 0); };
  const toggle = () => { if (playing) { ref.current?.pause(); setPlaying(false); } else play(i); };
  return (
    <div className="panel p-5 max-w-2xl" data-testid="poem-playlist">
      <div className="flex items-center gap-3 mb-3">
        <button onClick={toggle} className="w-11 h-11 rounded-full bg-amber-600 hover:bg-amber-500 flex items-center justify-center transition-colors" data-testid="playlist-toggle">{playing ? <Pause size={18} /> : <Play size={18} className="ml-0.5" />}</button>
        <div className="flex-1"><p className="eyebrow flex items-center gap-1"><ListMusic size={12} />Listen to the collection</p><p className="text-sm text-slate-200" data-testid="playlist-now">{tracks[i].title}</p></div>
        {b.playlist_download && <a href={`${API}/public/books/${b.slug}/poems/playlist.mp3`} className="text-xs inline-flex items-center gap-1 border border-amber-500/40 px-3 py-1.5 rounded-md text-amber-100 hover:bg-amber-600 transition-colors" data-testid="playlist-download"><Download size={13} />Download</a>}
      </div>
      <audio ref={ref} src={`${API}/public/books/${b.slug}/poems/${tracks[i].id}/audio`} onTimeUpdate={(e) => window.dispatchEvent(new CustomEvent("poem-time", { detail: { id: tracks[i].id, t: e.currentTarget.currentTime } }))} onEnded={() => { window.dispatchEvent(new CustomEvent("poem-time", { detail: { id: tracks[i].id, t: -1 } })); i + 1 < tracks.length ? play(i + 1) : setPlaying(false); }} onPause={() => setPlaying(false)} onPlay={() => setPlaying(true)} className="hidden" />
      <ol className="space-y-1">{tracks.map((t, n) => <li key={t.id}><button onClick={() => play(n)} className={`w-full text-left text-sm px-3 py-1.5 rounded-md ${n === i ? "bg-amber-500/15 text-amber-200" : "text-slate-400 hover:bg-slate-900"}`} data-testid={`playlist-track-${n}`}>{n + 1}. {t.title}</button></li>)}</ol>
    </div>
  );
}

export function PoemSamples({ b }) {
  if (!b.poems?.length) return null;
  return (
    <section className="space-y-10" data-testid="poem-samples" onPlayCapture={(e) => document.querySelectorAll("[data-testid=poem-samples] audio").forEach((a) => a !== e.target && a.pause())}>
      <div><p className="eyebrow mb-2">Read a few poems</p><h2 className="font-display text-3xl">From the collection</h2></div>
      <Playlist b={b} />
      {b.poems.map((p) => <PoemCard key={p.id} b={b} p={p} />)}
    </section>
  );
}

function PoemCard({ b, p }) {
  const [t, setT] = useState(-1);
  useEffect(() => {
    const on = (e) => e.detail.id === p.id && setT(e.detail.t);
    window.addEventListener("poem-time", on);
    return () => window.removeEventListener("poem-time", on);
  }, [p.id]);
  const cur = [...(p.timing || [])].reverse().find((x) => t >= x.start && t >= 0)?.line;
  return (
    <article className="panel p-6 sm:p-10 max-w-2xl" data-testid={`public-poem-${p.id}`}>
      {p.has_illustration && <img src={`${API}/public/books/${b.slug}/poems/${p.id}/illustration`} alt="" className="w-full rounded mb-6" />}
      <h3 className="font-display text-2xl mb-4">{p.title}</h3>
      {p.epigraph && <p className="text-sm italic text-slate-400 text-right mb-4">{p.epigraph}</p>}
      {p.has_audio && <audio controls preload="none" src={`${API}/public/books/${b.slug}/poems/${p.id}/audio`} onTimeUpdate={(e) => setT(e.currentTarget.currentTime)} onEnded={() => setT(-1)} className="w-full mb-5" data-testid={`public-poem-audio-${p.id}`} />}
      <div className="font-serif text-slate-200 leading-8" data-testid={`public-poem-body-${p.id}`}>
        {p.body.split("\n").map((ln, i) => (
          <p key={i} className={`whitespace-pre-wrap min-h-[1rem] rounded px-1 -mx-1 transition-colors duration-300 ${cur === i ? "bg-amber-400/20 text-amber-100" : ""}`} data-testid={cur === i ? "poem-line-active" : undefined}>{ln || " "}</p>
        ))}
      </div>
    </article>
  );
}

export function ReadPreview({ b }) {
  if (!b.comic?.preview_pages && !b.comic?.next_episode) return null;
  const nx = b.comic.next_episode;
  return (
    <div className="mt-6 flex flex-col items-start gap-4">
      {b.comic.preview_pages > 0 && <Link to={`/authors/${b.slug}/read`} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-amber-600 hover:bg-amber-500 text-white text-sm transition-colors" data-testid="read-preview-link">
        <BookOpen size={15} />{b.format === "webtoon" ? `Scroll ${b.comic.preview_pages} free panels` : `Read ${b.comic.preview_pages} free pages${b.comic.direction === "rtl" ? " (right to left)" : ""}`}
      </Link>}
      {nx && <div className="panel px-4 py-3 inline-flex flex-col gap-1" data-testid="showcase-next-episode"><p className="text-xs text-slate-400">Episode {nx.episode} arrives in</p><Countdown unlocks={nx.unlocks} /></div>}
    </div>
  );
}
